/**
 * Screen 4 — Shows & Programs.
 *
 * Search plus category chips. A sold-out programme stays in the list, in
 * grey: removing it makes the member think it was cancelled, and they ring
 * a committee member to ask.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { LocalisedField } from "../components/Localised";
import { ProgramArt } from "../components/ProgramArt";
import { ErrorNote, Note, Shell } from "../components/Shell";
import type { Locale } from "../i18n";
import { api, isSignedIn, OfflineError, type MetaResponse, type ProgramSummary } from "../lib/api";
import { formatDateTime, formatIdr } from "../lib/format";

export default function Programs({ locale }: { locale: Locale }) {
  const { t } = useTranslation();
  const [programs, setPrograms] = useState<ProgramSummary[] | null>(null);
  const [meta, setMeta] = useState<MetaResponse | null>(null);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  // Which programmes this member already holds a ticket for. Showing
  // "Book now" to someone who booked last week is the kind of small blindness
  // that makes an app feel like a form rather than a service.
  const [ticketed, setTicketed] = useState<Set<string>>(new Set());

  useEffect(() => {
    api.meta(locale).then(setMeta).catch(() => undefined);
    if (isSignedIn()) {
      api
        .myBookings(locale)
        .then((b) =>
          setTicketed(
            new Set(
              b.upcoming
                .filter((x) => x.state === "PAID" || x.state === "CHECKED_IN")
                .map((x) => x.program.slug),
            ),
          ),
        )
        .catch(() => undefined);
    }
  }, [locale]);

  useEffect(() => {
    let cancelled = false;
    // A short debounce so typing does not fire a request per keystroke on a
    // phone data plan.
    const timer = setTimeout(() => {
      api
        .programs({ q: query, category: category || undefined }, locale)
        .then((body) => {
          if (!cancelled) {
            setPrograms(body.programs);
            setError(null);
          }
        })
        .catch((err) => {
          if (!cancelled) setError(err instanceof OfflineError ? t("error.network") : t("error.generic"));
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, category, locale, t]);

  return (
    <Shell masthead area="programs" title={t("programs.title")}>
      <input
        className="field"
        type="search"
        value={query}
        placeholder={t("programs.search")}
        onChange={(e) => setQuery(e.target.value)}
        aria-label={t("programs.search")}
      />

      <div className="chips">
        <button
          type="button"
          className={`chip${category === "" ? " chip--on" : ""}`}
          onClick={() => setCategory("")}
        >
          {t("programs.all")}
        </button>
        {(meta?.categories ?? []).map((c) => (
          <button
            key={c.code}
            type="button"
            className={`chip${category === c.code ? " chip--on" : ""}`}
            onClick={() => setCategory(c.code)}
          >
            {t(c.label_key)}
          </button>
        ))}
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}
      {programs === null && !error && <Note>{t("common.loading")}</Note>}
      {programs?.length === 0 && <Note>{t("programs.none")}</Note>}

      <ul className="cards">
        {(programs ?? []).map((p) => (
          <li key={p.id} className={`card card--art${p.sold_out ? " card--sold-out" : ""}`}>
            <Link className="card__art" to={`/programs/${p.slug}`} aria-hidden="true" tabIndex={-1}>
              <ProgramArt
                poster={p.poster_url}
                category={p.category}
                labelKey={p.category_label_key}
              />
            </Link>
            {ticketed.has(p.slug) && <span className="badge badge--own">{t("programs.you_have_a_ticket")}</span>}
            <LocalisedField content={p.text} field="name" as="h2" className="card__title" note />
            {/* Date and venue on separate lines at different weights, the
                way a printed programme sets them. */}
            <p className="card__meta card__when">{formatDateTime(p.date_time, locale)}</p>
            <p className="card__meta">{p.venue}</p>
            <p className="card__meta">
              {p.from_price_idr !== null && (
                <span className="money">
                  {t("programs.from_price", { price: formatIdr(p.from_price_idr) })}
                </span>
              )}
              {p.members_only && <span className="badge"> {t("programs.members_only")}</span>}
            </p>
            {p.sold_out ? (
              <span className="badge badge--muted">{t("programs.sold_out")}</span>
            ) : (
              <Link className="btn" to={`/programs/${p.slug}`}>
                {t("programs.view_details")}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </Shell>
  );
}
